/**
 * Sticky Left Scroll Right Section
 * Desktop: Left column is sticky; right blocks scroll with the page.
 *          When you stop scrolling inside this section, we smooth-scroll
 *          to align the nearest block to the top (no document scroll-snap,
 *          so you can scroll away freely).
 * Mobile: Standard carousel behavior.
 */
import Component from '../vendor/Component'

const SNAP_DEBOUNCE_MS = 120
const REDUCED_MOTION = typeof window !== 'undefined' &&
  window.matchMedia('(prefers-reduced-motion: reduce)').matches

export default class StickyLeftScrollRight extends Component {
  constructor(elem, theme, options, ctx) {
    super(elem, theme, options, ctx)

    this.section = elem
    this.blocks = []

    this._onScroll = null
    this._onResize = null
    this._snapTimeout = null
    this._raf = null
  }

  async mount () {
    if (window.innerWidth < 1200) return
    if (REDUCED_MOTION) return

    this.blocks = Array.from(this.section.querySelectorAll('.sec-StickyLeftScrollRight_Block'))
    if (!this.blocks.length) return

    this._onScroll = this._onScrollHandler.bind(this)
    this._onResize = this._onResizeHandler.bind(this)

    window.addEventListener('scroll', this._onScroll, { passive: true })
    window.addEventListener('resize', this._onResize, { passive: true })
  }

  unmount () {
    if (this._snapTimeout) {
      clearTimeout(this._snapTimeout)
      this._snapTimeout = null
    }
    if (this._raf) {
      cancelAnimationFrame(this._raf)
      this._raf = null
    }
    if (this._onScroll) {
      window.removeEventListener('scroll', this._onScroll)
      this._onScroll = null
    }
    if (this._onResize) {
      window.removeEventListener('resize', this._onResize)
      this._onResize = null
    }
  }

  _onScrollHandler () {
    if (this._snapTimeout) clearTimeout(this._snapTimeout)
    this._snapTimeout = setTimeout(() => {
      this._snapTimeout = null
      if (this._raf) return
      this._raf = requestAnimationFrame(() => {
        this._raf = null
        this._snapToNearestBlock()
      })
    }, SNAP_DEBOUNCE_MS)
  }

  _onResizeHandler () {
    if (window.innerWidth < 1200) return
  }

  _snapToNearestBlock () {
    const scrollY = window.scrollY || window.pageYOffset
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight
    const sectionRect = this.section.getBoundingClientRect()
    const sectionTop = scrollY + sectionRect.top
    const sectionBottom = sectionTop + sectionRect.height

    const scrollBottom = scrollY + viewportHeight
    if (scrollY < sectionTop || scrollBottom > sectionBottom) return

    let bestBlock = null
    let bestDistance = Infinity

    for (let i = 0; i < this.blocks.length; i++) {
      const rect = this.blocks[i].getBoundingClientRect()
      const blockTop = scrollY + rect.top
      const distance = Math.abs(blockTop - scrollY)

      if (distance < bestDistance) {
        bestDistance = distance
        bestBlock = this.blocks[i]
      }
    }

    if (!bestBlock) return

    const targetRect = bestBlock.getBoundingClientRect()
    const targetScrollY = scrollY + targetRect.top

    if (Math.abs(targetScrollY - scrollY) < 2) return

    window.scrollTo({ top: targetScrollY, behavior: 'smooth' })
  }
}
